/**
 * MarketingToolsModal — Iter 191x
 *
 * A single reusable Squarespace-style side-panel modal that hosts the
 * Announcement Banner editor and the Promotional Pop-Up editor.
 *
 * Layout: full-viewport overlay with a 340-px left rail (inputs) + right
 * area (site preview via iframe of the storefront). Reads + writes to
 * `settings.announcement` and `settings.promotional_popup` via
 * /api/builder/settings.
 *
 * Usage:
 *   <MarketingToolsModal kind="announcement" onClose={…} />
 *   <MarketingToolsModal kind="popup"        onClose={…} />
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X as XIcon, ChevronRight, ChevronLeft, Loader2, ExternalLink,
  Megaphone, Layout, Sparkles, Clock, Palette,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULT_ANNOUNCEMENT = {
  enabled: false,
  text: 'Free shipping on orders over $75',
  link_url: '',
  link_text: 'Shop now',
  background: '#0F0F23',
  text_color: '#FFFFFF',
  dismissible: true,
  show_on_mobile: true,
};

const DEFAULT_POPUP = {
  enabled: false,
  layout: 'center',
  action: {
    mode: 'newsletter',
    email_placeholder: 'Email Address',
    submit_label: 'Subscribe',
    disclaimer: 'We respect your privacy.',
    button_label: 'Learn more',
    button_url: '',
  },
  content: {
    headline: 'Join our mailing list.',
    body: 'Be the first to know about new arrivals, sales, exclusive offers, and special events.',
  },
  display: {
    pages: 'any_first',
    timing_mode: 'timer',
    timer_seconds: 5,
    scroll_percent: 25,
    frequency_days: 30,
    hide_after_signup: true,
    show_on_mobile: true,
  },
  style: {
    headline_color: '#0F172A',
    headline_font: '',
    body_color: '#475569',
    body_font: '',
    background: '#FFFFFF',
    color_behind_text: '',
    button_color: '#0F172A',
    button_text_color: '#FFFFFF',
    button_font: '',
    animation: 'fade',
    // Close button
    close_style: 'icon',       // 'icon' | 'text'
    close_location: 'inside',  // 'inside' | 'outside'
    close_color: '#94A3B8',
    // Overlay design
    advanced_layout: false,
  },
  mobile: {
    override_enabled: true,
    color_behind_text: '',
    headline_font: '',
    headline_color: '#0F172A',
    body_font: '',
    body_color: '#475569',
    button_font: '',
    button_color: '#0F172A',
    button_text_color: '#FFFFFF',
  },
};

// Default palette used by the "Reset styles to defaults" button
const POPUP_STYLE_DEFAULTS = { ...DEFAULT_POPUP.style };
const POPUP_MOBILE_DEFAULTS = { ...DEFAULT_POPUP.mobile };

export default function MarketingToolsModal({ kind, subdomain: propSubdomain, onClose }) {
  const [config, setConfig] = useState(kind === 'announcement' ? DEFAULT_ANNOUNCEMENT : DEFAULT_POPUP);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [section, setSection] = useState('root'); // root | action | content | display | style
  // Iter 191z — resolve the vendor's real subdomain so the iframe shows the correct site
  const [subdomain, setSubdomain] = useState(propSubdomain || '');

  useEffect(() => {
    // Fetch the site so we know the exact subdomain — the parent may have passed a stale value.
    (async () => {
      try {
        const { data: site } = await axios.get(`${API}/builder/my-site`, { headers: auth() });
        if (site?.subdomain) setSubdomain(site.subdomain);
      } catch (_) { /* silent */ }
    })();

    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/settings`, { headers: auth() });
        const key = kind === 'announcement' ? 'announcement' : 'promotional_popup';
        const existing = data?.[key];
        if (existing && Object.keys(existing).length > 0) {
          setConfig({ ...(kind === 'announcement' ? DEFAULT_ANNOUNCEMENT : DEFAULT_POPUP), ...existing });
        }
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [kind]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = kind === 'announcement' ? { announcement: config } : { promotional_popup: config };
      await axios.put(`${API}/builder/settings`, payload, { headers: auth() });
      toast.success(kind === 'announcement' ? 'Announcement saved' : 'Pop-up saved');
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const previewUrl = useMemo(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return subdomain ? `${base.replace('/api', '')}/site/${subdomain}` : '';
  }, [subdomain]);

  const isPopup = kind === 'popup';
  const title = isPopup ? 'Promotional Pop-Up' : 'Announcement Banner';

  return (
    <div className="fixed inset-0 z-[80] flex bg-[#08081A]" data-testid={`marketing-modal-${kind}`}>
      {/* ═════ LEFT RAIL ═════ */}
      <aside className="w-[360px] flex-shrink-0 bg-[#171A2C] text-white overflow-y-auto flex flex-col border-r border-white/[0.06]" data-testid="marketing-modal-panel">
        {section === 'root' ? (
          <>
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
              <button onClick={onClose} className="text-xs uppercase tracking-[0.14em] font-bold text-white/60 hover:text-white flex items-center gap-1.5 transition-colors" data-testid="marketing-modal-close">
                <ChevronLeft size={14} /> Pages
              </button>
              <button onClick={save} disabled={saving || loading} className="text-xs uppercase tracking-[0.14em] font-bold px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-40 transition-all" data-testid="marketing-modal-save">
                {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
              </button>
            </div>

            <div className="p-6">
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, Inter, sans-serif' }} data-testid="marketing-modal-title">{title}</h1>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">
                {isPopup
                  ? 'Create a pop-up that displays when a visitor opens your website.'
                  : 'Show a thin bar above your site header — perfect for promotions or announcements.'}
              </p>

              <label className="flex items-center justify-between mt-6 py-3 border-b border-white/[0.06] cursor-pointer">
                <span className="text-sm font-medium text-white">Display {isPopup ? 'Pop-Up' : 'Banner'}</span>
                <Switch checked={!!config.enabled} onChange={(v) => setConfig({ ...config, enabled: v })} testid={`marketing-modal-enabled-${kind}`} />
              </label>

              {isPopup ? (
                <>
                  <MiniPreviewCard popup={config} />
                  <MenuLink label="Action"          desc="Choose a newsletter form or button" onClick={() => setSection('action')} Icon={Sparkles} />
                  <MenuLink label="Content"         desc="Add a headline and text"            onClick={() => setSection('content')} Icon={Layout} />
                  <MenuLink label="Display & Timing" desc="Control when the pop-up appears"   onClick={() => setSection('display')} Icon={Clock} />
                  <MenuLink label="Style"           desc="Change fonts, colors, and animations" onClick={() => setSection('style')} Icon={Palette} />
                </>
              ) : (
                <AnnouncementFields config={config} setConfig={setConfig} />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
              <button onClick={() => setSection('root')} className="text-xs uppercase tracking-[0.14em] font-bold text-white/60 hover:text-white flex items-center gap-1.5 transition-colors" data-testid="marketing-modal-back">
                <ChevronLeft size={14} /> {title}
              </button>
              <button onClick={save} disabled={saving} className="text-xs uppercase tracking-[0.14em] font-bold px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-40 transition-all">
                {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
              </button>
            </div>
            <div className="p-6">
              {section === 'action'  && <PopupActionSection  config={config} setConfig={setConfig} />}
              {section === 'content' && <PopupContentSection config={config} setConfig={setConfig} />}
              {section === 'display' && <PopupDisplaySection config={config} setConfig={setConfig} subdomain={subdomain} />}
              {section === 'style'   && <PopupStyleSection   config={config} setConfig={setConfig} />}
            </div>
          </>
        )}
      </aside>

      {/* ═════ RIGHT — live preview ═════ */}
      <main className="flex-1 relative bg-[#0E0E23]">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 grid place-items-center rounded-lg bg-white/[0.08] backdrop-blur border border-white/10 text-white hover:bg-white/[0.15] transition-colors" aria-label="Close" data-testid="marketing-modal-close-x">
          <XIcon size={16} />
        </button>
        {previewUrl && (
          <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.08] backdrop-blur border border-white/10 text-white/85 text-xs font-medium">
            <span>Preview · <span className="text-[#A78BFA]">{subdomain || '—'}</span></span>
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors">
              <ExternalLink size={11} />
            </a>
          </div>
        )}
        {previewUrl ? (
          <iframe
            key={`${subdomain}:${JSON.stringify(config)}`}   // remount to reflect saved config + subdomain
            src={previewUrl}
            title={`${title} preview`}
            className="w-full h-full border-0"
            data-testid="marketing-modal-preview"
          />
        ) : (
          <div className="h-full grid place-items-center text-white/60 text-sm">
            {loading ? 'Loading your site…' : 'Save your site once to see a live preview.'}
          </div>
        )}
      </main>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// Left-rail form sections
// ═════════════════════════════════════════════════════════════════

function AnnouncementFields({ config, setConfig }) {
  const set = (k, v) => setConfig({ ...config, [k]: v });
  return (
    <div className="space-y-4 mt-4">
      <Field label="Message text">
        <TextInput value={config.text} onChange={(v) => set('text', v)} placeholder="Free shipping on orders over $75" testid="ann-text" />
      </Field>
      <Field label="Optional link">
        <TextInput value={config.link_url} onChange={(v) => set('link_url', v)} placeholder="https://example.com/sale" testid="ann-link-url" />
      </Field>
      <Field label="Link text">
        <TextInput value={config.link_text} onChange={(v) => set('link_text', v)} placeholder="Shop now" testid="ann-link-text" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Background"><ColorInput value={config.background} onChange={(v) => set('background', v)} testid="ann-bg" /></Field>
        <Field label="Text color"><ColorInput value={config.text_color} onChange={(v) => set('text_color', v)} testid="ann-fg" /></Field>
      </div>
      <label className="flex items-center justify-between py-2 cursor-pointer">
        <span className="text-sm text-white/80">Show the dismiss (×) button</span>
        <Switch checked={!!config.dismissible} onChange={(v) => set('dismissible', v)} testid="ann-dismissible" />
      </label>
      <label className="flex items-center justify-between py-2 cursor-pointer">
        <span className="text-sm text-white/80">Show on mobile</span>
        <Switch checked={!!config.show_on_mobile} onChange={(v) => set('show_on_mobile', v)} testid="ann-mobile" />
      </label>
    </div>
  );
}

function PopupActionSection({ config, setConfig }) {
  const act = config.action || {};
  const set = (patch) => setConfig({ ...config, action: { ...act, ...patch } });
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase font-bold tracking-wider text-white/55 mb-2">GOAL</p>
        <p className="text-xs text-white/55 mb-3">What do you want visitors to do with your pop-up?</p>
        <RadioRow selected={act.mode || 'newsletter'} onChange={(v) => set({ mode: v })} options={[
          { id: 'newsletter', label: 'Sign up for a newsletter' },
          { id: 'button',     label: 'Click a button' },
        ]} testid="popup-action-mode" />
      </div>

      {(act.mode || 'newsletter') === 'newsletter' ? (
        <>
          <Field label="Email field placeholder"><TextInput value={act.email_placeholder} onChange={(v) => set({ email_placeholder: v })} placeholder="Email Address" testid="popup-email-placeholder" /></Field>
          <Field label="Submit button label"><TextInput value={act.submit_label} onChange={(v) => set({ submit_label: v })} placeholder="Subscribe" testid="popup-submit-label" /></Field>
          <Field label="Disclaimer" desc="Shown below the input field.">
            <textarea 
value={act.disclaimer || ''} onChange={(e) => set({ disclaimer: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 focus:border-[#A78BFA]/60 focus:ring-2 focus:ring-[#A78BFA]/25 focus:bg-white/[0.08] outline-none text-white placeholder-white/30 text-sm resize-y transition-all" placeholder="We respect your privacy." data-testid="popup-disclaimer" />
          </Field>
        </>
      ) : (
        <>
          <Field label="Button label"><TextInput value={act.button_label} onChange={(v) => set({ button_label: v })} placeholder="Learn more" testid="popup-btn-label" /></Field>
          <Field label="Button URL"><TextInput value={act.button_url} onChange={(v) => set({ button_url: v })} placeholder="https://…" testid="popup-btn-url" /></Field>
        </>
      )}
    </div>
  );
}

function PopupContentSection({ config, setConfig }) {
  const set = (patch) => setConfig({ ...config, content: { ...(config.content || {}), ...patch } });
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>Branding &amp; Text</h2>
        <p className="text-xs text-white/55 mt-1">Craft a headline that will grab attention and keep your body message clear and concise.</p>
      </div>
      <Field label="Headline"><TextInput value={config.content?.headline} onChange={(v) => set({ headline: v })} placeholder="Join our mailing list." testid="popup-headline-input" /></Field>
      <Field label="Body">
        <textarea 
value={config.content?.body || ''} onChange={(e) => set({ body: e.target.value })} rows={5} className="w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 focus:border-[#A78BFA]/60 focus:ring-2 focus:ring-[#A78BFA]/25 focus:bg-white/[0.08] outline-none text-white placeholder-white/30 text-sm resize-y transition-all" placeholder="Be the first to know about new arrivals, sales, exclusive offers, and special events." data-testid="popup-body-input" />
      </Field>
    </div>
  );
}

function PopupDisplaySection({ config, setConfig, subdomain }) {
  const dsp = config.display || {};
  const set = (patch) => setConfig({ ...config, display: { ...dsp, ...patch } });

  const resetSeen = () => {
    try {
      localStorage.removeItem(`promotional_popup_seen:${subdomain || 'site'}`);
      localStorage.removeItem(`promotional_popup_subscribed:${subdomain || 'site'}`);
    } catch (_) { /* ignore */ }
    toast.success('Local dismissal reset — the pop-up will show on your next visit.');
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase font-bold tracking-wider text-white/55 mb-2">PAGES</p>
        <p className="text-xs text-white/55 mb-3">Choose the page(s) where the pop-up may appear.</p>
        <Select value={dsp.pages || 'any_first'} onChange={(v) => set({ pages: v })} options={[
          { id: 'any_first', label: 'Any First Page' },
          { id: 'home_only', label: 'Homepage Only' },
        ]} testid="popup-pages" />
      </div>

      <div>
        <p className="text-xs uppercase font-bold tracking-wider text-white/55 mb-2">TIMING</p>
        <p className="text-xs text-white/55 mb-3">Choose when to display the pop-up.</p>
        <Select value={dsp.timing_mode || 'timer'} onChange={(v) => set({ timing_mode: v })} options={[
          { id: 'timer',      label: 'Show on a Timer' },
          { id: 'scroll',     label: 'Show on Scroll' },
          { id: 'immediate',  label: 'Show immediately' },
        ]} testid="popup-timing-mode" />
        {(dsp.timing_mode || 'timer') === 'timer' && (
          <div className="mt-3">
            <Select value={String(dsp.timer_seconds ?? 5)} onChange={(v) => set({ timer_seconds: parseInt(v) })} options={[
              { id: '2', label: 'After 2 Seconds' },
              { id: '5', label: 'After 5 Seconds' },
              { id: '10', label: 'After 10 Seconds' },
              { id: '20', label: 'After 20 Seconds' },
            ]} testid="popup-timer-seconds" />
          </div>
        )}
        {dsp.timing_mode === 'scroll' && (
          <div className="mt-3">
            <Select value={String(dsp.scroll_percent ?? 25)} onChange={(v) => set({ scroll_percent: parseInt(v) })} options={[
              { id: '10', label: 'After 10% scroll' },
              { id: '25', label: 'After 25% scroll' },
              { id: '50', label: 'After 50% scroll' },
              { id: '75', label: 'After 75% scroll' },
            ]} testid="popup-scroll-percent" />
          </div>
        )}
      </div>

      <div>
        <p className="text-xs uppercase font-bold tracking-wider text-white/55 mb-2">FREQUENCY</p>
        <p className="text-xs text-white/55 mb-3">After a visitor sees the pop-up, show it again:</p>
        <Select value={String(dsp.frequency_days ?? 30)} onChange={(v) => set({ frequency_days: parseInt(v) })} options={[
          { id: '1',  label: 'Next day' },
          { id: '7',  label: 'In 7 days' },
          { id: '30', label: 'In 30 days' },
          { id: '90', label: 'In 90 days' },
        ]} testid="popup-frequency" />
        <label className="flex items-center gap-2 mt-4 cursor-pointer">
          <input type="checkbox" checked={!!dsp.hide_after_signup} onChange={(e) => set({ hide_after_signup: e.target.checked })} className="accent-[#A78BFA]" data-testid="popup-hide-after-signup" />
          <span className="text-sm text-white/80">Don&rsquo;t show again after newsletter signup</span>
        </label>
        <label className="flex items-center gap-2 mt-2 cursor-pointer">
          <input type="checkbox" checked={!!dsp.show_on_mobile} onChange={(e) => set({ show_on_mobile: e.target.checked })} className="accent-[#A78BFA]" data-testid="popup-show-mobile" />
          <span className="text-sm text-white/80">Show on mobile</span>
        </label>
      </div>

      <div className="pt-4 border-t border-white/[0.06]">
        <p className="text-xs uppercase font-bold tracking-wider text-white/55 mb-2">RESET</p>
        <p className="text-xs text-white/55 mb-3">Choose to show the pop-up again to all visitors the next time they visit. Useful when you create a new pop-up.</p>
        <button onClick={resetSeen} className="w-full py-2.5 rounded-md text-xs uppercase tracking-wider font-bold bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white hover:opacity-90" data-testid="popup-reset-btn">RESET</button>
      </div>
    </div>
  );
}

function PopupStyleSection({ config, setConfig }) {
  const st = config.style || {};
  const mob = config.mobile || {};
  const set = (patch) => setConfig({ ...config, style: { ...st, ...patch } });
  const setMob = (patch) => setConfig({ ...config, mobile: { ...mob, ...patch } });

  const resetStyles = () => {
    setConfig({ ...config, style: { ...POPUP_STYLE_DEFAULTS }, mobile: { ...POPUP_MOBILE_DEFAULTS } });
    toast.success('Styles reset to defaults');
  };
  const setSystemFonts = () => {
    setConfig({
      ...config,
      style:  { ...st,  headline_font: 'system-ui', body_font: 'system-ui', button_font: 'system-ui' },
      mobile: { ...mob, headline_font: 'system-ui', body_font: 'system-ui', button_font: 'system-ui' },
    });
    toast.success('All fonts set to system default');
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>Styles</h2>

      <p className="text-xs uppercase font-bold tracking-wider text-white/55">HEADLINE</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Font"><TextInput value={st.headline_font} onChange={(v) => set({ headline_font: v })} placeholder="Syne" testid="popup-headline-font" /></Field>
        <Field label="Color"><ColorInput value={st.headline_color} onChange={(v) => set({ headline_color: v })} testid="popup-headline-color" /></Field>
      </div>

      <p className="text-xs uppercase font-bold tracking-wider text-white/55 pt-2">BODY</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Font"><TextInput value={st.body_font} onChange={(v) => set({ body_font: v })} placeholder="Inter" testid="popup-body-font" /></Field>
        <Field label="Color"><ColorInput value={st.body_color} onChange={(v) => set({ body_color: v })} testid="popup-body-color" /></Field>
      </div>

      <p className="text-xs uppercase font-bold tracking-wider text-white/55 pt-2">BACKGROUND</p>
      <Field label="Card background"><ColorInput value={st.background} onChange={(v) => set({ background: v })} testid="popup-bg" /></Field>
      <Field label="Color behind text (optional)"><ColorInput value={st.color_behind_text} onChange={(v) => set({ color_behind_text: v })} testid="popup-behind-text" /></Field>

      <p className="text-xs uppercase font-bold tracking-wider text-white/55 pt-2">BUTTON</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Font"><TextInput value={st.button_font} onChange={(v) => set({ button_font: v })} placeholder="Inter" testid="popup-btn-font" /></Field>
        <Field label="Background"><ColorInput value={st.button_color} onChange={(v) => set({ button_color: v })} testid="popup-btn-bg" /></Field>
      </div>
      <Field label="Text color"><ColorInput value={st.button_text_color} onChange={(v) => set({ button_text_color: v })} testid="popup-btn-fg" /></Field>

      <p className="text-xs uppercase font-bold tracking-wider text-white/55 pt-4">CLOSE BUTTON</p>
      <Field label="Style">
        <Select value={st.close_style || 'icon'} onChange={(v) => set({ close_style: v })} options={[
          { id: 'icon', label: 'Icon' },
          { id: 'text', label: 'Text ("Close")' },
        ]} testid="popup-close-style" />
      </Field>
      <Field label="Location">
        <Select value={st.close_location || 'inside'} onChange={(v) => set({ close_location: v })} options={[
          { id: 'inside',  label: 'Inside' },
          { id: 'outside', label: 'Outside' },
        ]} testid="popup-close-location" />
      </Field>
      <Field label="Color"><ColorInput value={st.close_color} onChange={(v) => set({ close_color: v })} testid="popup-close-color" /></Field>

      <p className="text-xs uppercase font-bold tracking-wider text-white/55 pt-4">OVERLAY ANIMATION</p>
      <Field label="Style">
        <Select value={st.animation || 'fade'} onChange={(v) => set({ animation: v })} options={[
          { id: 'fade',     label: 'Fade In' },
          { id: 'slide-up', label: 'Slide up' },
          { id: 'none',     label: 'No animation' },
        ]} testid="popup-animation" />
      </Field>

      <p className="text-xs uppercase font-bold tracking-wider text-white/55 pt-4">OVERLAY DESIGN</p>
      <Field label="Advanced Layout">
        <Select value={st.advanced_layout ? 'enabled' : 'disabled'} onChange={(v) => set({ advanced_layout: v === 'enabled' })} options={[
          { id: 'disabled', label: 'Disabled' },
          { id: 'enabled',  label: 'Enabled' },
        ]} testid="popup-advanced-layout" />
      </Field>

      <p className="text-xs uppercase font-bold tracking-wider text-white/55 pt-4">LAYOUT</p>
      <Field label="Position">
        <Select value={config.layout || 'center'} onChange={(v) => setConfig({ ...config, layout: v })} options={[
          { id: 'center',       label: 'Centered modal' },
          { id: 'lower_right',  label: 'Lower right corner' },
          { id: 'lower_banner', label: 'Bottom banner' },
        ]} testid="popup-layout" />
      </Field>

      {/* Mobile-specific overrides */}
      <div className="pt-4 border-t border-white/[0.06]">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase font-bold tracking-wider text-white/55">MOBILE</p>
          <Select value={mob.override_enabled ? 'enabled' : 'disabled'} onChange={(v) => setMob({ override_enabled: v === 'enabled' })} options={[
            { id: 'enabled',  label: 'Enabled' },
            { id: 'disabled', label: 'Disabled' },
          ]} testid="popup-mobile-override" />
        </div>
        <p className="text-[11px] text-white/55 mb-3">Override styles just for mobile visitors. Leave any field blank to inherit from the desktop styles above.</p>

        {mob.override_enabled && (
          <div className="space-y-3">
            <Field label="Color behind text"><ColorInput value={mob.color_behind_text} onChange={(v) => setMob({ color_behind_text: v })} testid="popup-mobile-behind-text" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Headline font"><TextInput value={mob.headline_font} onChange={(v) => setMob({ headline_font: v })} placeholder="Syne" testid="popup-mobile-headline-font" /></Field>
              <Field label="Headline color"><ColorInput value={mob.headline_color} onChange={(v) => setMob({ headline_color: v })} testid="popup-mobile-headline-color" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Body font"><TextInput value={mob.body_font} onChange={(v) => setMob({ body_font: v })} placeholder="Inter" testid="popup-mobile-body-font" /></Field>
              <Field label="Body color"><ColorInput value={mob.body_color} onChange={(v) => setMob({ body_color: v })} testid="popup-mobile-body-color" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Button font"><TextInput value={mob.button_font} onChange={(v) => setMob({ button_font: v })} placeholder="Inter" testid="popup-mobile-btn-font" /></Field>
              <Field label="Button color"><ColorInput value={mob.button_color} onChange={(v) => setMob({ button_color: v })} testid="popup-mobile-btn-bg" /></Field>
            </div>
            <Field label="Button text color"><ColorInput value={mob.button_text_color} onChange={(v) => setMob({ button_text_color: v })} testid="popup-mobile-btn-fg" /></Field>
          </div>
        )}
      </div>

      {/* Reset actions */}
      <div className="pt-4 border-t border-white/[0.06] space-y-2">
        <button
          type="button"
          onClick={resetStyles}
          className="w-full py-3 rounded-lg text-xs uppercase tracking-[0.14em] font-bold bg-white/[0.06] hover:bg-white/[0.1] text-white/85 border border-white/10 transition-colors"
          data-testid="popup-reset-styles"
        >
          Reset Styles to Defaults
        </button>
        <button
          type="button"
          onClick={setSystemFonts}
          className="w-full py-3 rounded-lg text-xs uppercase tracking-[0.14em] font-bold bg-white/[0.06] hover:bg-white/[0.1] text-white/85 border border-white/10 transition-colors"
          data-testid="popup-system-fonts"
        >
          Set All to System Fonts
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// Small building blocks
// ═════════════════════════════════════════════════════════════════

function Field({ label, desc, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.14em] font-bold text-white/60 mb-1.5">{label}</label>
      {desc && <p className="text-[11px] text-white/50 mb-1.5">{desc}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, testid }) {
  return (
    <input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 focus:border-[#A78BFA]/60 focus:ring-2 focus:ring-[#A78BFA]/25 focus:bg-white/[0.08] outline-none text-white placeholder-white/30 text-sm transition-all"
      data-testid={testid}
    />
  );
}

function ColorInput({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="w-9 h-9 rounded border border-white/15 cursor-pointer bg-transparent" data-testid={`${testid}-picker`} />
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="#000000" className="flex-1 px-2 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 focus:border-[#A78BFA]/60 outline-none text-white text-sm font-mono uppercase placeholder-white/30" data-testid={`${testid}-hex`} />
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 focus:border-[#A78BFA]/60 outline-none text-white text-sm transition-all" data-testid={testid}>
      {options.map((o) => <option key={o.id} value={o.id} style={{ background: '#0E0E23', color: 'white' }}>{o.label}</option>)}
    </select>
  );
}

function RadioRow({ selected, onChange, options, testid }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      {options.map((o) => (
        <label key={o.id} className="flex items-center gap-2 cursor-pointer">
          <input type="radio" checked={selected === o.id} onChange={() => onChange(o.id)} className="accent-[#A78BFA]" />
          <span className="text-sm text-white/85">{o.label}</span>
        </label>
      ))}
    </div>
  );
}

function Switch({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${checked ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6]' : 'bg-white/15'}`}
      data-testid={testid}
    >
      <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  );
}

function MenuLink({ label, desc, onClick, Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full mt-2 flex items-center justify-between text-left px-4 py-3.5 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.06] hover:border-white/[0.12] transition-all"
      data-testid={`popup-menu-${label.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')}`}
    >
      <div className="flex items-center gap-3">
        {Icon && <Icon size={15} className="text-[#A78BFA]" />}
        <div>
          <div className="text-xs uppercase tracking-[0.14em] font-bold text-white/85">{label}</div>
          <div className="text-[11px] text-white/50 mt-0.5">{desc}</div>
        </div>
      </div>
      <ChevronRight size={14} className="text-white/40" />
    </button>
  );
}

function MiniPreviewCard({ popup }) {
  const cfg = popup || {};
  const st  = cfg.style || {};
  return (
    <div className="mt-6 p-4 rounded-lg bg-white/[0.03] border border-white/[0.08]">
      <div className="grid place-items-center h-32 relative">
        <div className="w-3/4 max-w-[180px] p-3 rounded-md shadow-lg" style={{ background: st.background || '#ffffff', color: st.body_color || '#0F172A' }}>
          <p className="text-[10px] font-bold text-center leading-tight" style={{ color: st.headline_color || '#0F172A' }}>{cfg.content?.headline || 'Join our mailing list.'}</p>
          <div className="mt-1.5 h-1.5 rounded bg-slate-200 w-4/5 mx-auto" />
          <div className="mt-2 h-3 rounded" style={{ background: st.button_color || '#0F172A' }} />
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-[0.14em] text-white/50 font-bold text-center mt-2">Change layout</p>
    </div>
  );
}
